/**
 * « Lancement des produits » — the platform launch switches (no board draws it; decision: it lives in the
 * Facturation space, ADMIN only, capability "platform"). Invoicing ships dark on web: turning it on reveals the
 * 3-service designs (App-Inscription, MX-Choix-Service, MX2–MX7), the menus, guides and tours to every client, at once.
 * Turning it off hides everything again; nothing is deleted. Every toggle is audited (who, when, why).
 * OWNED BY area (g) — saas: restyle freely, keep the contract (PATCH /admin/platform/features) and the confirmation.
 */
import { useState, type ReactNode } from "react";
import { createRoute } from "@tanstack/react-router";
import { BookOpen, Eye, EyeOff, History, LayoutDashboard, ListChecks, Lock, Rocket, ShieldCheck, UserPlus, Database } from "lucide-react";
import { useApiMutation, useApiQuery } from "@/api/hooks";
import type { ApiError } from "@/api/client";
import { saasAdminApi, type PlatformFeatureEvent } from "@/contracts/areas/saas";
import { ADMIN_ONLY_HINT } from "@/contracts/permissions";
import { guard, staffRoute } from "@/app/route-base";
import { useCan } from "@/auth/session";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Card } from "@/ui/card";
import { Checkbox, Field, Switch, Textarea } from "@/ui/field";
import { PageHeader, ReadOnlyBanner } from "@/ui/page";
import { EmptyState, Skeleton } from "@/ui/states";
import { Table, TD, TH, THead, TR } from "@/ui/table";
import { useToast } from "@/ui/toast";
import { Window } from "@/ui/window";
import { useOnline } from "@/ui/offline-strip";
import { cn } from "@/lib/cn";
import { LoadError, Notice, SectionHead, WindowFooter, fmtDate, fmtWhen } from "./ui";

/** What a client sees, switch off vs on (the contract: shared/platform.ts « PlatformFeatures.invoicing »). */
const EFFECTS: { icon: ReactNode; label: string; off: string; on: string }[] = [
  { icon: <UserPlus className="size-4" />, label: "Inscription", off: "2 services proposés : création et comptabilité.", on: "3 services proposés, facturation comprise." },
  { icon: <ListChecks className="size-4" />, label: "Choix du service", off: "Le sélecteur ne montre pas la facturation.", on: "La facturation et ses plans START, PRO et BUSINESS apparaissent." },
  { icon: <LayoutDashboard className="size-4" />, label: "Menus et tableau de bord", off: "Aucune entrée « Facturation » ; les pages /app/facturation sont inaccessibles.", on: "Entrée « Facturation » dans le menu et le tableau de bord des comptes qui ont le service." },
  { icon: <BookOpen className="size-4" />, label: "Guides et visites guidées", off: "Les guides et visites de la facturation sont masqués.", on: "Les guides et visites guidées de la facturation sont proposés." },
  { icon: <Database className="size-4" />, label: "Données des comptes", off: "Conservées telles quelles (factures, devis, clients, abonnements), simplement invisibles.", on: "Conservées et de nouveau visibles par leurs propriétaires." },
];

function PlatformPage() {
  const can = useCan();
  const writer = can("platform", "write");
  const online = useOnline();
  const q = useApiQuery(saasAdminApi.platformFeatures);
  const h = useApiQuery(saasAdminApi.platformHistory);
  const [confirm, setConfirm] = useState<boolean | null>(null);
  const header = (
    <PageHeader
      kicker="Facturation SaaS · plateforme"
      title="Lancement des produits"
      subtitle="Chaque produit est livré éteint. L'allumer le rend visible à tous les clients, sur le site et dans l'app, immédiatement. L'éteindre le masque à nouveau sans rien supprimer."
    />
  );
  if (q.isPending)
    return (
      <div>
        {header}
        <Card className="space-y-4" role="status" aria-label="Chargement…">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-40 w-full" />
        </Card>
      </div>
    );
  if (q.isError)
    return (
      <div>
        {header}
        <LoadError what="l'état des produits n'a pas pu être chargé." onRetry={() => q.refetch()} />
      </div>
    );
  const on = q.data.invoicing;
  const last = h.data?.events.find((e) => e.feature === "invoicing");
  return (
    <div>
      {!writer && <ReadOnlyBanner />}
      {header}
      <Card className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-[1_1_320px] items-start gap-3.5">
            <span className={cn("inline-flex size-11 shrink-0 items-center justify-center rounded-full", on ? "bg-success-bg text-success" : "bg-neutral text-ink-2")}>
              <Rocket className="size-5" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-[17px] font-extrabold text-ink">Facturation</h2>
                <Badge tone={on ? "success" : "muted"} dot className="max-w-full whitespace-normal text-left">
                  {on ? "Lancée · visible par tous les clients" : "Éteinte · invisible pour les clients"}
                </Badge>
              </div>
              <p className="mt-1 text-[12.5px] leading-relaxed text-muted">
                Logiciel de facturation Melrys (factures, devis, avoirs, facture électronique via Iopole).{" "}
                {last ? (
                  <>
                    {last.enabled ? "Lancée" : "Éteinte"} le {fmtDate(last.at)} à {fmtWhen(last.at).split(" ").pop()} par <strong className="text-ink-2">{last.by.name}</strong>.
                  </>
                ) : h.isPending ? null : (
                  "Jamais basculée depuis la mise en service."
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5" title={writer ? undefined : ADMIN_ONLY_HINT}>
            <Switch checked={on} disabled={!writer || !online} onChange={(v) => setConfirm(v)} label={<span className="font-bold">{on ? "Activée" : "Désactivée"}</span>} />
            {!writer && (
              <span className="inline-flex items-center gap-1 text-[11.5px] text-muted">
                <Lock className="size-3" /> {ADMIN_ONLY_HINT}
              </span>
            )}
          </div>
        </div>
        <div>
          <h3 className="mb-2.5 text-[13px] font-extrabold text-ink">Ce que voient les clients</h3>
          <div className="overflow-hidden rounded-[14px] border border-line">
            <div className="hidden grid-cols-[200px_minmax(0,1fr)_minmax(0,1fr)] bg-canvas text-[11px] font-bold uppercase tracking-[0.06em] text-muted md:grid">
              <div className="px-4 py-2.5" />
              <div className={cn("flex items-center gap-1.5 px-4 py-2.5", !on && "text-ink")}>
                <EyeOff className="size-3.5" /> Facturation éteinte {!on && <Badge size="sm" tone="dark">Aujourd'hui</Badge>}
              </div>
              <div className={cn("flex items-center gap-1.5 px-4 py-2.5", on && "text-ink")}>
                <Eye className="size-3.5" /> Facturation lancée {on && <Badge size="sm" tone="dark">Aujourd'hui</Badge>}
              </div>
            </div>
            <ul className="divide-y divide-line">
              {EFFECTS.map((e) => (
                <li key={e.label} className="grid gap-x-0 md:grid-cols-[200px_minmax(0,1fr)_minmax(0,1fr)]">
                  <div className="flex items-center gap-2 px-4 pt-3 text-[13px] font-bold text-ink md:py-3">
                    <span className="text-primary-500">{e.icon}</span>
                    {e.label}
                  </div>
                  <div className={cn("px-4 py-1.5 text-[12.5px] leading-relaxed md:py-3", !on ? "bg-primary-50/60 text-ink" : "text-muted")}>
                    <span className="font-semibold md:hidden">Éteinte : </span>
                    {e.off}
                  </div>
                  <div className={cn("px-4 pb-3 pt-1.5 text-[12.5px] leading-relaxed md:py-3", on ? "bg-primary-50/60 text-ink" : "text-muted")}>
                    <span className="font-semibold md:hidden">Lancée : </span>
                    {e.on}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Card>

      <Card className="mt-5">
        <SectionHead kicker="Journal d'audit" title="Historique des bascules" subtitle="Chaque bascule est enregistrée : qui, quand, dans quel sens et pourquoi (ADMIN_FEATURE_FLAG_SET)." />
        {h.isPending ? (
          <div className="space-y-3" role="status" aria-label="Chargement…">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : h.isError ? (
          <LoadError what="le journal n'a pas pu être chargé." onRetry={() => h.refetch()} />
        ) : h.data.events.length === 0 ? (
          <EmptyState art={<History className="size-6 text-ink-2" />} title="Aucune bascule">
            La facturation n'a encore jamais été allumée ni éteinte.
          </EmptyState>
        ) : (
          <HistoryList events={h.data.events} />
        )}
      </Card>

      {writer && <ConfirmWindow target={confirm} accounts={h.data?.invoicingAccounts ?? null} onClose={() => setConfirm(null)} />}
    </div>
  );
}

function ActionBadge({ e }: { e: PlatformFeatureEvent }) {
  return (
    <Badge tone={e.enabled ? "success" : "neutral"} size="sm" icon={e.enabled ? <Eye className="size-3" /> : <EyeOff className="size-3" />}>
      {e.enabled ? "Lancée" : "Éteinte"}
    </Badge>
  );
}

function HistoryList({ events }: { events: PlatformFeatureEvent[] }) {
  return (
    <>
      <div className="hidden md:block">
        <Table className="[&_td]:px-3 [&_th]:px-3">
          <THead>
            <tr>
              <TH>Date</TH>
              <TH>Produit</TH>
              <TH>Action</TH>
              <TH>Par</TH>
              <TH>Motif</TH>
              <TH>Comptes facturation</TH>
            </tr>
          </THead>
          <tbody>
            {events.map((e) => (
              <TR key={e.id}>
                <TD className="whitespace-nowrap">
                  <div className="font-semibold">{fmtDate(e.at)}</div>
                  <div className="text-[12px] text-muted">{fmtWhen(e.at).split(" ").pop()}</div>
                </TD>
                <TD>Facturation</TD>
                <TD>
                  <ActionBadge e={e} />
                </TD>
                <TD>
                  <div className="font-semibold">{e.by.name}</div>
                  {e.by.role && <div className="text-[12px] text-muted">{e.by.role}</div>}
                </TD>
                <TD className="min-w-[200px] text-[12.5px] text-ink-2">{e.note ?? <span className="text-subtle">—</span>}</TD>
                <TD className="tabular-nums">{e.accountsAtToggle ?? "—"}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </div>
      <ul className="divide-y divide-line rounded-[14px] border border-line md:hidden">
        {events.map((e) => (
          <li key={e.id} className="p-3.5">
            <div className="flex flex-wrap items-center gap-2">
              <ActionBadge e={e} />
              <span className="text-[12.5px] font-semibold text-ink">
                {fmtDate(e.at)} · {fmtWhen(e.at).split(" ").pop()}
              </span>
            </div>
            <div className="mt-1 text-[12.5px] text-ink-2">
              par {e.by.name}
              {e.by.role ? ` (${e.by.role})` : ""}
              {e.accountsAtToggle !== null ? ` · ${e.accountsAtToggle} comptes facturation` : ""}
            </div>
            {e.note && <div className="mt-1 text-[12.5px] text-muted [overflow-wrap:anywhere]">« {e.note} »</div>}
          </li>
        ))}
      </ul>
    </>
  );
}

function ConfirmWindow({ target, accounts, onClose }: { target: boolean | null; accounts: number | null; onClose: () => void }) {
  const toast = useToast();
  const online = useOnline();
  const [note, setNote] = useState("");
  const [ack, setAck] = useState(false);
  const m = useApiMutation(saasAdminApi.setPlatformFeatures, { invalidates: [saasAdminApi.platformFeatures, saasAdminApi.platformHistory, saasAdminApi.overview] });
  const close = () => {
    if (m.isPending) return;
    setNote("");
    setAck(false);
    m.reset();
    onClose();
  };
  const launch = target === true;
  const err = m.error as ApiError | null;
  return (
    <Window
      open={target !== null}
      onClose={close}
      locked={m.isPending}
      width={600}
      icon={launch ? <Rocket className="size-[18px]" /> : <EyeOff className="size-[18px]" />}
      title={launch ? "Lancer la facturation pour tous les clients ?" : "Éteindre la facturation ?"}
      subtitle="L'effet est immédiat, sur le site et dans l'app, pour tous les clients. La bascule est enregistrée dans le journal d'audit."
    >
      <div className="space-y-4 px-6 pb-5 pt-5">
        <div>
          <h3 className="mb-2 text-[13px] font-extrabold text-ink">{launch ? "Dès la confirmation, les clients verront :" : "Dès la confirmation, les clients ne verront plus :"}</h3>
          <ul className="space-y-2">
            {EFFECTS.slice(0, 4).map((e) => (
              <li key={e.label} className="flex gap-2.5 text-[12.5px] leading-relaxed">
                <span className="mt-0.5 shrink-0 text-primary-500">{e.icon}</span>
                <span className="min-w-0">
                  <strong className="text-ink">{e.label}</strong> — <span className="text-ink-2">{launch ? e.on : e.off}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <Notice tone={launch ? "info" : "warning"}>
          {launch ? (
            <>
              <ShieldCheck className="mr-1 inline size-3.5 align-[-2px]" />
              {accounts !== null ? `${accounts} comptes facturation existent déjà : ` : "Les comptes facturation existants : "}
              leurs données sont conservées et redeviennent visibles par leurs propriétaires.
            </>
          ) : (
            <>
              Rien n'est supprimé ni résilié : {accounts !== null ? `les ${accounts} comptes facturation` : "les comptes facturation"} gardent leurs factures, devis, clients et abonnements. Tout réapparaît dès que vous rallumez la facturation. Les clients en
              cours d'utilisation perdent l'accès immédiatement.
            </>
          )}
        </Notice>
        <Field label="Motif (facultatif)" hint="Enregistré dans le journal d'audit avec votre nom et l'heure, jamais montré aux clients.">
          <Textarea rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder={launch ? "Lancement public de la facturation." : "Incident Iopole en cours : on masque le temps de corriger."} />
        </Field>
        <Checkbox checked={ack} onChange={(e) => setAck(e.target.checked)} label={launch ? "J'ai compris : tous les clients verront la facturation immédiatement." : "J'ai compris : la facturation disparaît immédiatement pour tous les clients."} />
        {err && <Notice tone="danger">{err.code === "unchanged" ? `${err.message} Rechargez la page pour voir l'état actuel.` : err.status >= 500 || err.status === 0 ? "« Une erreur est survenue » — rien n'a changé pour les clients. Réessayez." : err.message}</Notice>}
      </div>
      <WindowFooter>
        <Button variant="outline" size="lg" onClick={close} disabled={m.isPending}>
          Annuler
        </Button>
        <Button
          size="lg"
          variant={launch ? "primary" : "danger-solid"}
          loading={m.isPending}
          disabled={!ack || !online}
          onClick={() =>
            m.mutate(
              { body: { invoicing: launch, note: note.trim() || undefined } },
              {
                onSuccess: (f) => {
                  toast(f.invoicing ? "Facturation lancée" : "Facturation éteinte", "success", f.invoicing ? "Les clients voient la facturation dès maintenant." : "La facturation est masquée ; les données sont conservées.");
                  close();
                },
              },
            )
          }
        >
          {launch ? "Lancer la facturation" : "Éteindre la facturation"}
        </Button>
      </WindowFooter>
    </Window>
  );
}

export const plateformeRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/plateforme",
  beforeLoad: guard("platform", "read"),
  component: PlatformPage,
});

export const platformRoutes = [plateformeRoute] as const;

